import { useTranslation } from "react-i18next";
import { Card } from "../../components/ui";
import { localeFor } from "../../lib/format";

/** Pieces shared by the lifecycle screens. Extracted when the one long page became six, so the status colours
 * and the stat tile stay identical across them instead of drifting apart one screen at a time. */

export const PILL = {
  green: "bg-green-50 text-status-green border-status-green/40",
  amber: "bg-amber-50 text-status-amber border-status-amber/40",
  red: "bg-red-50 text-status-red border-status-red/40",
  blue: "bg-blue-50 text-brand-primary border-brand-primary/40",
  grey: "bg-brand-grey-light text-brand-charcoal border-brand-grey-light",
};

export const STATUS_PILL: Record<string, string> = {
  Open: PILL.blue, Replied: PILL.blue, Shortlisted: PILL.amber, Hold: PILL.grey, Rejected: PILL.red, Accepted: PILL.green,
  Pending: PILL.blue, "Under Review": PILL.amber, Cleared: PILL.green, Cancelled: PILL.grey, "Awaiting Response": PILL.amber,
  Completed: PILL.green, "In Process": PILL.blue, Draft: PILL.blue, Approved: PILL.green, Closed: PILL.grey,
  Active: PILL.green, Left: PILL.grey,
};

export const pillClass = (status: string | null | undefined) =>
  `rounded border px-2 py-0.5 text-[11px] font-semibold ${STATUS_PILL[status ?? ""] ?? PILL.grey}`;

/** Dates arrive as `YYYY-MM-DD` (or a datetime); render them in the reader's locale, never as a raw string. */
export function useDateFormat() {
  const { i18n } = useTranslation();
  const fmt = new Intl.DateTimeFormat(localeFor(i18n.language), { dateStyle: "medium" });
  return (s: string | null | undefined) => (s ? fmt.format(new Date(`${s.slice(0, 10)}T00:00:00`)) : "–");
}

export function Stat({ label, value, cls = "", hint }: { label: string; value: number | string; cls?: string; hint?: string }) {
  return (
    <Card className="px-4 py-3">
      <div className={`font-heading text-2xl font-semibold ${cls}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-brand-grey">{label}</div>
      {hint && <div className="mt-0.5 text-[11px] text-brand-grey">{hint}</div>}
    </Card>
  );
}

export function SectionCard({ title, aside, children }: { title: string; aside?: string; children: React.ReactNode }) {
  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-brand-grey-light px-4 py-3">
        <h2 className="font-heading text-base font-semibold">{title}</h2>
        {aside && <span className="text-xs text-brand-grey">{aside}</span>}
      </div>
      {children}
    </Card>
  );
}
